
/*--------------------------------------------------------------------------------------------
Program     :  PayloadCompare.js
Description :  Main program for compare the basePayload and combinedData payload.
Date        :  27/10/2025 
Created By  :  Jelbin
---------------------------------------------------------------------------------------------
TicketNo        Author          Date            ChangeDesc
---------------------------------------------------------------------------------------------
27102025       -Jelbin     -  27/10/2025         Created
---------------------------------------------------------------------------------------------*/
// ---------------------------------------------------------------------------
// Function Purpose and main logic:
// ---------------------------------------------------------------------------
// Loop through each user in the base payload and compare them against
// the corresponding modified payload user. Identify any changes that
// require travel time recalculation and mark them appropriately.
//
// ---------------------------------------------------------------------------
// Marking Rules:
// ---------------------------------------------------------------------------
// - At the **user level**, set:   RECALCULATETRVLTIME = "Y"
// - At the **task level**, set:   ZTRAVELTIMECALC = "Y"
//
// ---------------------------------------------------------------------------
// Change Detection Scenarios:
// ---------------------------------------------------------------------------
// 1. **POI (Points of Interest) Changes at User Level**
//    - POI added, modified, or removed (by POIASSIGNDATE or UNKdata)
//    - No task travel time recalculation. Only POI travel time calculation from last task
//
// 2. **Task Order or Movement Changes**
//    - Task order changed within a user
//    - Task added or removed between users
//    - Marks all tasks for the affected date.
//
// 3. **POI Changes Inside a Task**
//    - Any difference detected in pointsOfInterest within a task detail
//    - Marks all tasks for that task’s date.
//
// 4. **Participant Assignment Changes**
//    - Any difference in participant fields (USERLOGIN2–USERLOGIN5)
//      compared between base and modified tasks
//    - Marks all tasks for that date
//
// ---------------------------------------------------------------------------
// Result:
// ---------------------------------------------------------------------------
// Returns the modified payload where each affected user and task
// is tagged for travel time recalculation.
// After return the modified payload calcualte the travel time
// ---------------------------------------------------------------------------

import React, { useEffect } from "react";

const startPayloadjsonCompare = (basePayload, combinedData, ZDATERANGE,
    tableUniqueName, mergeparticipantFields, selectedtraveler) => {
    // Deep copies (safe way)
    const basePayloadCopy = structuredClone(basePayload);
    const combinedDataCopy = structuredClone(combinedData);

    // --------------------------------------------------
    // Helper: Compare two POI arrays deeply
    // --------------------------------------------------
    const hasPOIChanged = (basePOI = [], modPOI = []) => {
        if (basePOI.length !== modPOI.length) return true;
        return basePOI.some((poi, idx) => JSON.stringify(poi) !== JSON.stringify(modPOI[idx]));
    };

    // --------------------------------------------------------------
    // Helper: Mark for recalculation during POI changes in user level
    // --------------------------------------------------------------

    const markTravelTimePOI = (user, date, poiName) => {
        user.USERPOICHANGED = "Y";
        user.RECALCULATETRVLTIME = "Y";

        // Initialize POIChangedDates array if not exists
        if (!Array.isArray(user.POIChangedDates)) {
            user.POIChangedDates = [];
        }
        // Normalize date to YYYY-MM-DD
        const poiDate = date?.slice(0, 10);
        // Add two new fields in an array in the userlvel if date not already recorded
        if (poiDate && !user.POIChangedDates.some(p => p.POIDate === poiDate)) {
            user.POIChangedDates.push({ POIDate: poiDate, POIUNKData: poiName });
        }
    };
    // -------------------------------------------------------------------------------------
    // Helper: Mark tasks for recalculation during task changes & POI changes in task level
    // -------------------------------------------------------------------------------------
    const markTravelTime = (user, date, custNotes = []) => {
        // Loop through all task details for this user
        (user.details || []).forEach(d => {
            const taskDate = d[ZDATERANGE].slice(0, 10);
            // Match by date, and optionally by custNotes list
            if (taskDate === date && (custNotes.length === 0 || custNotes.includes(d[tableUniqueName]))) {
                d.ZTRAVELTIMECALC = "Y"; // dynamically create this field
            }
        });
        user.RECALCULATETRVLTIME = "Y"; // new user-level field
    };

    // Helper: Mark tasks where only participant changes occurred
    const markTravelTimeParticipants = (user, date, custNotes = []) => {
        let taskMarked = false;
        (user.details || []).forEach(d => {
            const taskDate = d[ZDATERANGE].slice(0, 10);
            if (taskDate === date && (custNotes.length === 0 || custNotes.includes(d[tableUniqueName]))) {
                d.ZPARTITRAVELTIMECALC = "Y"; // new participant-specific travel flag
                taskMarked = true; // record that at least one task was flagged
            }
        });
        // If at least one task was flagged, mark user-level recalculation
        if (taskMarked) {
            user.RECALCULATETRVLTIME = "Y"; // new user-level field
        }
    };

    // ---------------------------------------------------------------
    // Main Comparison : loop in basePayloadCopy through each user
    // ---------------------------------------------------------------
    basePayloadCopy.forEach(baseUser => {
        const modUser = combinedDataCopy.find(u => u[selectedtraveler] === baseUser[selectedtraveler]);
        if (!modUser) return;

        const basePOI = baseUser.pointsOfInterest || [];
        const modPOI = modUser.pointsOfInterest || [];

        // --------------------------------------------------
        // Step 1: Compare POI blocks in user  level (by POIASSIGNDATE + UNKdata)
        // --------------------------------------------------

        // Step 1.1 — detect added or changed POIs in user level
        modPOI.forEach(poi => {
            const poiAssignDate = poi.POIASSIGNDATE;
            const poiName = poi.UNKdata;
            if (!poiAssignDate) return;

            const basePOIBlock = basePOI.find(
                b => b.POIASSIGNDATE === poiAssignDate && b.UNKdata === poiName
            );
            if (!basePOIBlock || hasPOIChanged(basePOIBlock.pointsOfInterest, poi.pointsOfInterest)) {
                console.log(`POI added/changed for user ${modUser[selectedtraveler]} on ${poiAssignDate} (${poiName})`);
                markTravelTimePOI(modUser, poiAssignDate, poiName);
            }
        });

        // Pass 2 — detect removed POIs in user level
        basePOI.forEach(poi => {
            const poiAssignDate = poi.POIASSIGNDATE;
            const poiName = poi.UNKdata;
            if (!poiAssignDate) return;

            const modPOIBlock = modPOI.find(
                m => m.POIASSIGNDATE === poiAssignDate && m.UNKdata === poiName
            );
            if (!modPOIBlock) {
                console.log(`POI removed for user ${modUser[selectedtraveler]} on ${poiAssignDate} (${poiName})`);
                markTravelTimePOI(modUser, poiAssignDate, "");
            }
        });

        // --------------------------------------------------
        // Step 2: Compare task order or movement by CURDATE
        // --------------------------------------------------
        const baseDates = [...new Set((baseUser.details || []).map(d => d[ZDATERANGE].slice(0, 10)))];
        const modDates = [...new Set((modUser.details || []).map(d => d[ZDATERANGE].slice(0, 10)))];

        // All the dates from basedate & mode date
        const alltaskDates = [...new Set([...baseDates, ...modDates])];

        // loop through each dates within the user and filter the details for that date from base and mod
        // all the below logic uder this loop. 
        // alltaskDates array creates based on the user from the main loop
        alltaskDates.forEach(date => {
            const baseDetailsForDate = (baseUser.details || []).filter(
                d => d[ZDATERANGE].slice(0, 10) === date
            );
            const modDetailsForDate = (modUser.details || []).filter(
                d => d[ZDATERANGE].slice(0, 10) === date
            );

            const baseNotes = baseDetailsForDate.map(d => d[tableUniqueName]);
            const modNotes = modDetailsForDate.map(d => d[tableUniqueName]);

            // find changed order or added or removed
            const orderChanged =
                baseNotes.length !== modNotes.length ||   // if any task added or removed between user
                baseNotes.some((note, idx) => note !== modNotes[idx]); // if task rearranged within the user

            if (orderChanged) {
                console.log(`Detected change in task order for ${modUser[selectedtraveler]} on ${date}`);
                markTravelTime(modUser, date, modNotes);
           
                // Do participant recalculation if modUser has participant
                markTravelTimeParticipants(modUser, date);

            }

            // --------------------------------------------------
            // Step 3: Check POI differences inside each task detail
            // --------------------------------------------------
            modDetailsForDate.forEach(modTask => {
                const baseTask = baseDetailsForDate.find(b => b[tableUniqueName] === modTask[tableUniqueName]);
                const poiAssignDate = modTask[ZDATERANGE].slice(0, 10);

                // If task POI exists in modified
                if ((modTask.pointsOfInterest || []).length > 0) {
                    if (!baseTask) {
                        // New task — mark it
                        markTravelTime(modUser, poiAssignDate);
                        console.log(`New POI Added in task ${modTask[tableUniqueName]} (${modUser[selectedtraveler]})`);
                    } else if (
                        hasPOIChanged(baseTask.pointsOfInterest, modTask.pointsOfInterest)
                    ) {
                        console.log(`POI changed in task ${modTask[tableUniqueName]} (${modUser[selectedtraveler]})`);
                        markTravelTime(modUser, poiAssignDate);
                    }
                }

                // If task POI removed (was in base but not in mod)
                if (baseTask && (baseTask.pointsOfInterest || []).length > 0 &&
                    (!modTask.pointsOfInterest || modTask.pointsOfInterest.length === 0)) {
                    console.log(`POI removed in task ${modTask[tableUniqueName]} (${modUser[selectedtraveler]})`);
                    markTravelTime(modUser, poiAssignDate);
                }
            });

            // -----------------------------------------------------------------------
            // Step 4: Compare participant fields (USERLOGIN2–USERLOGIN5)
            // -----------------------------------------------------------------------
            // If order  changed in Step2, participant recalculation is already handled
            // No need to run this block again
            // This block will run only when Add/Remove any Participant from task without reorder the task

            if (!orderChanged) {
                const partiFields = mergeparticipantFields.map(item => item.ZPARTCIPANTFILED);

                // .some() — loops through each task in modDetailsForDate
                //It will stop as soon as one difference is found (because .some() returns true immediately when a condition is met).
                const partcipantChanged = modDetailsForDate.some(modTask => {
                    const baseTask = baseDetailsForDate.find(b => b[tableUniqueName] === modTask[tableUniqueName]);

                    // If the task is new and not in baseTask and if any participant fields filled with data,
                    // it triggers true.
                    if (!baseTask && partiFields.some(field => !!modTask[field])) {
                        return true;
                    }

                    // Existing task — check for value in paricipants field. if any difference -return true
                    return baseTask && partiFields.some(field => (modTask[field] || "") !== (baseTask[field] || ""));
                });

                if (partcipantChanged) {
                    console.log(`participant changed for ${modUser[selectedtraveler]} on ${date}`);
                    markTravelTimeParticipants(modUser, date);
                }
            }
        });
    });

    // Return updated combinedData (with dynamic ZTRAVELTIMECALC flags)
    return combinedDataCopy;
};

// ------------------------------
// Main component
// ------------------------------
const PayloadCompare = ({ basePayload, combinedData, ZDATERANGE, tableUniqueName,
    mergeparticipantFields, selectedtraveler, handlePayloadCompareResult }) => {
    useEffect(() => {
        if (!basePayload?.length || !combinedData?.length) return;

        const resultPayload = startPayloadjsonCompare(basePayload, combinedData, ZDATERANGE,
            tableUniqueName[0], mergeparticipantFields, selectedtraveler);

        // Send resultPayload back to Rightsidebar.js
        if (handlePayloadCompareResult) handlePayloadCompareResult(resultPayload);
    }, [basePayload, combinedData, handlePayloadCompareResult]);

    return null; // no UI — processing only
};

export default PayloadCompare;
